import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Comment, TaskDetail } from '@witty-board/shared'
import { useMeQuery } from '../../api/auth.js'
import { useUsersQuery } from '../../api/users.js'
import { useProjectsQuery } from '../../api/projects.js'
import { useCreateCommentMutation, useDeleteCommentMutation, useUpdateCommentMutation } from '../../api/comments.js'
import { Avatar } from '../../components/Avatar.js'
import { Markdown } from '../../components/Markdown.js'
import { describeActivity } from '../../lib/activity.js'
import { CommentComposer } from './CommentComposer.js'

interface TaskActivityThreadProps {
  task: TaskDetail
}

type ThreadEntry =
  | { kind: 'comment'; id: string; createdAt: string; comment: Comment }
  | { kind: 'activity'; id: string; createdAt: string; activity: TaskDetail['activity'][number] }

/** Comentarios y el resto de `activity`, en un único hilo cronológico (CLAUDE.md §6). */
export function TaskActivityThread({ task }: TaskActivityThreadProps) {
  const { t, i18n } = useTranslation()
  const me = useMeQuery()
  const users = useUsersQuery()
  const projects = useProjectsQuery()
  const createComment = useCreateCommentMutation(task.id)
  const updateComment = useUpdateCommentMutation(task.id)
  const deleteComment = useDeleteCommentMutation(task.id)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)

  const usersById = useMemo(() => new Map((users.data ?? []).map((user) => [user.id, user] as const)), [users.data])
  const projectsById = useMemo(
    () => new Map((projects.data ?? []).map((project) => [project.id, project] as const)),
    [projects.data],
  )

  const entries = useMemo<ThreadEntry[]>(() => {
    const comments: ThreadEntry[] = task.comments.map((comment) => ({
      kind: 'comment',
      id: comment.id,
      createdAt: comment.createdAt,
      comment,
    }))
    const activity: ThreadEntry[] = task.activity
      .filter((entry) => entry.type !== 'commented')
      .map((entry) => ({ kind: 'activity', id: entry.id, createdAt: entry.createdAt, activity: entry }))
    return [...comments, ...activity].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  }, [task.comments, task.activity])

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
    [i18n.language],
  )

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-meta font-medium text-text-muted">{t('task.detail.threadTitle')}</h2>

      <div className="flex flex-col gap-3">
        {entries.map((entry) => {
          if (entry.kind === 'activity') {
            const actor = usersById.get(entry.activity.actorId)
            const description = describeActivity(entry.activity, {
              t,
              users: usersById,
              projects: projectsById,
              locale: i18n.language,
            })
            return (
              <p key={entry.id} className="text-meta text-text-muted">
                <span className="text-text">{actor?.name ?? '—'}</span> {description} ·{' '}
                {dateFormatter.format(new Date(entry.createdAt))}
              </p>
            )
          }

          const comment = entry.comment
          const author = usersById.get(comment.authorId)
          const isAuthor = me.data?.id === comment.authorId
          const isEditing = editingCommentId === comment.id

          return (
            <div key={entry.id} className="flex gap-2">
              {author && <Avatar name={author.name} avatarUrl={author.avatarUrl} size="md" />}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center gap-2 text-meta text-text-muted">
                  <span className="font-medium text-text">{author?.name ?? '—'}</span>
                  <span>{dateFormatter.format(new Date(comment.createdAt))}</span>
                </div>

                {isEditing ? (
                  <CommentComposer
                    initialValue={comment.body}
                    onSubmit={(body) => {
                      updateComment.mutate({ id: comment.id, body })
                      setEditingCommentId(null)
                    }}
                    onCancel={() => setEditingCommentId(null)}
                  />
                ) : (
                  <>
                    <Markdown>{comment.body}</Markdown>
                    {isAuthor && (
                      <div className="flex gap-3 text-meta text-text-muted">
                        <button
                          type="button"
                          onClick={() => setEditingCommentId(comment.id)}
                          className="hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteComment.mutate(comment.id)}
                          className="hover:text-danger focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <CommentComposer onSubmit={(body) => createComment.mutate(body)} />
    </section>
  )
}
